/**
 * Academic Calendar Service
 * Provides academic calendar awareness and Early Decision priority handling
 */

class AcademicCalendarService {
  
  /**
   * Academic calendar periods to avoid for intensive tasks
   * Based on typical US academic calendar
   */
  static getAcademicConstraints(year = new Date().getFullYear()) {
    return {
      // Fall semester finals
      fallFinals: [
        { start: new Date(year, 11, 10), end: new Date(year, 11, 22), severity: 'high' }, // Dec 10-22
      ],
      
      // Spring semester finals  
      springFinals: [
        { start: new Date(year + 1, 4, 1), end: new Date(year + 1, 4, 15), severity: 'high' }, // May 1-15
      ],
      
      // Major holidays
      holidays: [
        { start: new Date(year, 10, 22), end: new Date(year, 10, 26), severity: 'medium' }, // Thanksgiving week
        { start: new Date(year, 11, 22), end: new Date(year + 1, 0, 8), severity: 'medium' }, // Winter break
        { start: new Date(year + 1, 2, 15), end: new Date(year + 1, 2, 22), severity: 'low' }, // Spring break (varies)
        { start: new Date(year + 1, 3, 15), end: new Date(year + 1, 3, 22), severity: 'low' }, // Easter week (varies)
      ],
      
      // AP Exam period
      apExams: [
        { start: new Date(year + 1, 4, 1), end: new Date(year + 1, 4, 15), severity: 'high' }, // May AP exams
      ],
      
      // SAT/ACT common test dates (avoid week before/after)
      testPrep: [
        { start: new Date(year, 9, 1), end: new Date(year, 9, 15), severity: 'low' }, // October SAT prep
        { start: new Date(year, 10, 1), end: new Date(year, 10, 15), severity: 'low' }, // November SAT prep  
        { start: new Date(year, 11, 1), end: new Date(year, 11, 8), severity: 'low' }, // December SAT prep
        { start: new Date(year + 1, 2, 1), end: new Date(year + 1, 2, 15), severity: 'low' }, // March SAT prep
        { start: new Date(year + 1, 4, 1), end: new Date(year + 1, 4, 15), severity: 'low' }, // May SAT prep
        { start: new Date(year + 1, 5, 1), end: new Date(year + 1, 5, 8), severity: 'low' }, // June SAT prep
      ]
    };
  }

  /**
   * Check if a date falls during an academic constraint period
   */
  static isAcademicConstraintPeriod(date, preferences = {}) {
    if (!preferences.academic_calendar_awareness) {
      return { isConstraint: false };
    }

    const targetDate = new Date(date);
    const year = targetDate.getFullYear();
    const constraints = this.getAcademicConstraints(year);
    
    // Check all constraint categories
    const allConstraints = [
      ...constraints.fallFinals.map(c => ({ ...c, type: 'finals', category: 'Fall Finals' })),
      ...constraints.springFinals.map(c => ({ ...c, type: 'finals', category: 'Spring Finals' })),
      ...constraints.holidays.map(c => ({ ...c, type: 'holiday', category: 'Holiday' })),
      ...constraints.apExams.map(c => ({ ...c, type: 'exams', category: 'AP Exams' })),
      ...constraints.testPrep.map(c => ({ ...c, type: 'test_prep', category: 'Test Prep' }))
    ];

    for (const constraint of allConstraints) {
      if (targetDate >= constraint.start && targetDate <= constraint.end) {
        return {
          isConstraint: true,
          constraint,
          recommendation: this.getConstraintRecommendation(constraint)
        };
      }
    }

    return { isConstraint: false };
  }

  /**
   * Get recommendation for handling academic constraints
   */
  static getConstraintRecommendation(constraint) {
    const recommendations = {
      high: {
        message: `Avoid scheduling intensive tasks during ${constraint.category.toLowerCase()}`,
        action: 'reschedule',
        bufferDays: 7
      },
      medium: {
        message: `Consider lighter workload during ${constraint.category.toLowerCase()}`,
        action: 'reduce_intensity',
        bufferDays: 3
      },
      low: {
        message: `Be aware of potential scheduling conflicts with ${constraint.category.toLowerCase()}`,
        action: 'monitor',
        bufferDays: 1
      }
    };

    return recommendations[constraint.severity] || recommendations.low;
  }

  /**
   * Adjust task scheduling based on academic calendar
   */
  static adjustTaskScheduling(tasks, preferences = {}) {
    if (!preferences.academic_calendar_awareness || !Array.isArray(tasks)) {
      return tasks;
    }

    return tasks.map(task => {
      const dueDate = new Date(task.due_date || task.deadline);
      const constraint = this.isAcademicConstraintPeriod(dueDate, preferences);
      
      if (constraint.isConstraint) {
        const adjustedDate = this.suggestAlternativeDate(dueDate, constraint, preferences);

        return {
          ...task,
          // Store original in metadata instead of non-existent column
          due_date: adjustedDate.toISOString(),
          scheduled_date: adjustedDate.toISOString(), // Both set to adjusted date initially
          metadata: {
            ...task.metadata,
            original_due_date: task.due_date || task.deadline,
            academic_adjustment: {
              reason: constraint.constraint.category,
              severity: constraint.constraint.severity,
              original_date: dueDate.toISOString(),
              adjustment_days: Math.ceil((adjustedDate - dueDate) / (1000 * 60 * 60 * 24))
            }
          }
        };
      }
      
      return task;
    });
  }

  /**
   * Suggest alternative date avoiding academic constraints
   */
  static suggestAlternativeDate(originalDate, constraint, preferences) {
    const bufferDays = constraint.recommendation.bufferDays;
    let suggestedDate = new Date(originalDate);
    
    // Move deadline earlier to avoid constraint period
    suggestedDate.setDate(suggestedDate.getDate() - bufferDays);
    
    // If the suggested date is too close to now, move it forward after the constraint
    const now = new Date();
    const minLeadTime = preferences.workload_capacity === 'intensive' ? 3 : 7; // days
    
    if (suggestedDate <= new Date(now.getTime() + minLeadTime * 24 * 60 * 60 * 1000)) {
      const constraintEnd = constraint.constraint.end;
      suggestedDate = new Date(constraintEnd);
      suggestedDate.setDate(suggestedDate.getDate() + bufferDays);
    }
    
    return suggestedDate;
  }

  /**
   * Early Decision Priority System
   * Prioritizes ED deadlines and adjusts other timelines accordingly
   */
  static applyEarlyDecisionPriority(colleges, preferences = {}) {
    if (!preferences.early_decision_priority || !Array.isArray(colleges)) {
      return colleges;
    }

    const edColleges = colleges.filter(college => 
      college.application_round === 'early_decision' || 
      college.application_round === 'early_decision_2'
    );

    if (edColleges.length === 0) {
      return colleges;
    }

    // ED deadlines (typically Nov 1 and Jan 1)
    const edDeadlines = {
      'early_decision': new Date(new Date().getFullYear(), 10, 1), // Nov 1
      'early_decision_2': new Date(new Date().getFullYear() + 1, 0, 1) // Jan 1
    };

    return colleges.map(college => {
      const isEDCollege = edColleges.some(ed => ed.id === college.id);
      
      if (isEDCollege) {
        return {
          ...college,
          priority_level: 'high',
          timeline_adjustment: {
            type: 'early_decision_priority',
            buffer_weeks: 2, // Extra 2 weeks buffer for ED applications
            focus_intensive: true
          }
        };
      } else {
        // Adjust other colleges to accommodate ED priority
        return {
          ...college,
          priority_level: college.application_round === 'early_action' ? 'medium' : 'normal',
          timeline_adjustment: {
            type: 'accommodates_ed_priority',
            delayed_start: edColleges.length > 0, // Start other apps after ED commitment
            buffer_weeks: 1
          }
        };
      }
    });
  }

  /**
   * Generate timeline recommendations based on academic calendar and ED priority
   */
  static generateTimelineRecommendations(colleges, preferences = {}, currentDate = new Date()) {
    const recommendations = [];
    
    // ED Priority recommendations
    if (preferences.early_decision_priority) {
      const edColleges = colleges.filter(c => 
        c.application_round === 'early_decision' || 
        c.application_round === 'early_decision_2'
      );
      
      if (edColleges.length > 0) {
        recommendations.push({
          type: 'ed_priority',
          priority: 'high',
          message: `Focus on Early Decision applications first (${edColleges.length} school${edColleges.length > 1 ? 's' : ''})`,
          action: 'prioritize_ed_deadlines',
          schools: edColleges.map(c => c.name),
          timeline: 'Complete ED applications 2 weeks before deadline'
        });
      }
    }

    // Academic calendar recommendations
    if (preferences.academic_calendar_awareness) {
      const nextConstraint = this.getNextAcademicConstraint(currentDate);
      if (nextConstraint) {
        const daysUntil = Math.ceil((nextConstraint.start - currentDate) / (1000 * 60 * 60 * 24));
        
        if (daysUntil <= 30) {
          recommendations.push({
            type: 'academic_calendar',
            priority: nextConstraint.severity === 'high' ? 'high' : 'medium',
            message: `${nextConstraint.category} approaching in ${daysUntil} days - plan accordingly`,
            action: 'adjust_workload',
            constraint: nextConstraint,
            suggestion: nextConstraint.severity === 'high' 
              ? 'Complete intensive tasks before this period'
              : 'Consider reduced workload during this time'
          });
        }
      }
    }

    // Workload capacity recommendations
    const totalColleges = colleges.length;
    const intensiveTasks = colleges.filter(c => c.priority_level === 'high').length;
    
    if (preferences.workload_capacity === 'light' && totalColleges > 6) {
      recommendations.push({
        type: 'workload_capacity',
        priority: 'medium',
        message: `You have ${totalColleges} colleges with light workload capacity - consider prioritizing`,
        action: 'reduce_college_list',
        suggestion: 'Focus on 4-6 top choice schools for optimal application quality'
      });
    }

    return recommendations.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  /**
   * Get the next academic constraint period
   */
  static getNextAcademicConstraint(currentDate = new Date()) {
    const year = currentDate.getFullYear();
    const constraints = this.getAcademicConstraints(year);
    
    const allConstraints = [
      ...constraints.fallFinals.map(c => ({ ...c, type: 'finals', category: 'Fall Finals' })),
      ...constraints.springFinals.map(c => ({ ...c, type: 'finals', category: 'Spring Finals' })),
      ...constraints.holidays.map(c => ({ ...c, type: 'holiday', category: 'Holiday' })),
      ...constraints.apExams.map(c => ({ ...c, type: 'exams', category: 'AP Exams' }))
    ];

    // Find the next constraint after current date
    const futureConstraints = allConstraints.filter(c => c.start > currentDate);
    
    if (futureConstraints.length > 0) {
      return futureConstraints.sort((a, b) => a.start - b.start)[0];
    }
    
    return null;
  }

  /**
   * Calculate workload distribution considering academic constraints
   */
  static distributeWorkload(tasks, preferences = {}) {
    if (!Array.isArray(tasks)) return tasks;

    const hoursPerWeek = preferences.available_hours_per_week || 8;
    const capacity = preferences.workload_capacity || 'moderate';
    
    // Capacity modifiers
    const capacityMultipliers = {
      'light': 0.7,
      'moderate': 1.0,
      'intensive': 1.4
    };
    
    const effectiveHours = Math.round(hoursPerWeek * capacityMultipliers[capacity]);
    
    return tasks.map(task => {
      const constraint = this.isAcademicConstraintPeriod(
        new Date(task.due_date || task.deadline), 
        preferences
      );
      
      let estimatedHours = task.estimated_hours || 2;
      
      // Reduce estimated work during constraint periods
      if (constraint.isConstraint) {
        const reductionFactors = {
          'high': 0.3,
          'medium': 0.7,
          'low': 0.9
        };
        estimatedHours *= reductionFactors[constraint.constraint.severity];
      }
      
      return {
        ...task,
        estimated_hours: Math.max(0.5, estimatedHours),
        effective_weekly_hours: effectiveHours,
        workload_adjustment: constraint.isConstraint ? {
          constraint_type: constraint.constraint.category,
          severity: constraint.constraint.severity,
          hours_reduced: task.estimated_hours ? (task.estimated_hours - estimatedHours) : 0
        } : null
      };
    });
  }
}

export default AcademicCalendarService;